import * as Slider from "@radix-ui/react-slider"
import { useTranslation } from "react-i18next"

export const TuningAdjustmentSlider = (props: {
	name: string
	value: number
	onChange: (value: number) => void
	min?: number
	max?: number
	leftLabel: string
	rightLabel: string
	disabled?: boolean
}) => {
	const { t } = useTranslation("components")
	const {
		value,
		onChange,
		min = -5,
		max = 5,
		leftLabel,
		rightLabel,
		disabled = false,
		name,
	} = props
	const steps = max - min
	const center = 0
	const centerPercent = ((center - min) / (max - min)) * 100

	return (
		<div className="flex w-full flex-col gap-2">
			<div className="relative flex-1">
				{/* Tick marks */}
				<div className="pointer-events-none absolute top-1/2 z-10 w-full -translate-y-1/2 px-2">
					<div className="flex w-full items-center justify-between">
						{Array.from({ length: steps + 1 }, (_, i) => {
							const tickValue = min + i
							const isInRange =
								(value >= center && tickValue >= center && tickValue <= value) ||
								(value < center && tickValue <= center && tickValue >= value)

							return (
								<div
									key={i}
									className={`size-1 rounded-full ${
										isInRange ? "bg-black dark:bg-white" : "bg-base-500"
									}`}
								/>
							)
						})}
					</div>
				</div>

				<Slider.Root
					className="relative flex size-5 w-full touch-none select-none items-center disabled:pointer-events-none disabled:opacity-50"
					value={[value]}
					onValueChange={(values) => onChange(values[0] ?? value)}
					min={min}
					max={max}
					step={1}
					disabled={disabled}
				>
					<Slider.Track className="relative mx-2 h-0.5 flex-1 rounded-full bg-base-500">
						<div className="absolute inset-0 overflow-hidden">
							<div
								className={`absolute h-full rounded-full ${
									disabled ? "bg-base-500" : "bg-black dark:bg-white"
								}`}
								style={{
									left: `${Math.min(
										centerPercent,
										((value - min) / (max - min)) * 100,
									)}%`,
									right: `${
										100 -
										Math.max(centerPercent, ((value - min) / (max - min)) * 100)
									}%`,
								}}
							/>
						</div>
					</Slider.Track>
					<Slider.Thumb
						aria-label={t("tuningAdjustmentSlider.tuneValueToSlider", {
							name: name,
							leftLabel: leftLabel,
							rightLabel: rightLabel,
						})}
						className={`relative block size-5 rounded-full border-2 bg-base-700 ${
							disabled ? "border-base-500" : "border-black dark:border-white"
						}`}
					>
						<div
							className={`absolute left-1/2 top-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full ${
								disabled ? "bg-base-500" : "bg-black dark:bg-white"
							}`}
						/>
					</Slider.Thumb>
				</Slider.Root>
			</div>

			<div
				aria-hidden
				className="flex w-full flex-row items-center justify-between text-xs text-base-400"
			>
				<span>{leftLabel}</span>
				<span>{rightLabel}</span>
			</div>
		</div>
	)
}
